import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
    ControlContainer,
    FormArray,
    FormControl,
    FormGroup,
    FormGroupDirective,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FacilityInformation } from '../../../../shared/models/organization-data.model';
import { pruneValue } from '../../../../shared/utils/prune-value.util';
import {
    AppFeaturesForm,
    AppForm,
    FacilityInformationFormControls,
    LatLngFormControls,
    OperatingHourFormControls,
} from '../../models/app-form';

@Component({
    selector: 'app-facility-information',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSlideToggleModule,
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        CommonModule,
    ],
    templateUrl: './facility-information-component.component.html',
    styleUrls: ['../../form-app.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class FacilityInformationComponent implements OnInit {
    constructor(private parent: FormGroupDirective) {}

    ngOnInit(): void {
        const features = this.featuresFormGroup;
        if (features) {
            if (!('facilityInformation' in features.controls)) {
                features.setControl('facilityInformation', this.createFacilityInformationFormArray());
            }
            if (!('showFacilityInformation' in features.controls)) {
                features.setControl('showFacilityInformation', new FormControl(false));
            }
        }
    }

    get parentForm(): FormGroup<AppForm> {
        const f = this.parent.form;
        return f as FormGroup<AppForm>;
    }

    get positionGroup(): FormGroup<LatLngFormControls> {
        return this.facilityInformationGroup.controls.position;
    }

    get facilitiesArray(): FormArray<FormGroup<FacilityInformationFormControls>> {
        const features = this.featuresFormGroup;
        if (!features.controls.facilityInformation) {
            features.setControl('facilityInformation', this.createFacilityInformationFormArray());
        }
        return features.controls.facilityInformation!;
    }

    private createFacilityInformationFormArray(): FormArray<FormGroup<FacilityInformationFormControls>> {
        return new FormArray<FormGroup<FacilityInformationFormControls>>([]);
    }

    public getFacilityGroup(index: number): FormGroup<FacilityInformationFormControls> {
        return this.facilitiesArray.at(index);
    }

    public getOperatingHoursArrayFor(index: number): FormArray<FormGroup<OperatingHourFormControls>> {
        return this.getFacilityGroup(index).controls.operatingHours;
    }

    public getPositionGroupFor(index: number): FormGroup<LatLngFormControls> {
        return this.getFacilityGroup(index).controls.position;
    }

    get facilityInformationGroup(): FormGroup<FacilityInformationFormControls> {
        const arr = this.facilitiesArray;
        if (!arr.length) {
            arr.push(this.createFacilityGroup());
        }
        return arr.at(0);
    }

    get featuresFormGroup(): FormGroup<AppFeaturesForm> {
        const pf = this.parentForm;
        if (!pf)
            throw new Error('FacilityInformationComponent must be used inside a FormGroupDirective with a parent form');
        const features = pf.controls.features;
        if (!features) throw new Error('features FormGroup is missing on parent form');
        return features;
    }

    get showFacilityInformationControl(): FormControl<boolean | null> {
        return this.featuresFormGroup.controls.showFacilityInformation!;
    }

    get airportNameControl(): FormControl<string | null> {
        return this.facilityInformationGroup.controls.airportName;
    }

    get airportCodeControl(): FormControl<string | null> {
        return this.facilityInformationGroup.controls.airportCode;
    }

    get operatingHoursArray(): FormArray<FormGroup<OperatingHourFormControls>> {
        return this.facilityInformationGroup.controls.operatingHours;
    }

    addOperatingHour(): void {
        this.operatingHoursArray.push(
            new FormGroup<OperatingHourFormControls>({
                days: new FormControl('', { nonNullable: true }),
                hour: new FormControl('', { nonNullable: true }),
            })
        );
    }

    removeOperatingHour(index: number): void {
        this.operatingHoursArray.removeAt(index);
    }

    addCustomer(): void {
        const arr = this.facilityInformationGroup.controls.customers;
        arr.push(new FormControl('', { nonNullable: true }));
    }

    addEmail(): void {
        const arr = this.facilityInformationGroup.controls.email;
        arr.push(new FormControl('', { nonNullable: true }));
    }

    addAwbPrefix(): void {
        const arr = this.facilityInformationGroup.controls.awbPrefixes;
        arr.push(new FormControl('', { nonNullable: true }));
    }

    // Create a fresh facility FormGroup
    public createFacilityGroup(): FormGroup<FacilityInformationFormControls> {
        return new FormGroup<FacilityInformationFormControls>({
            airportName: new FormControl(null),
            airportCode: new FormControl(null),
            address1: new FormControl(null),
            address2: new FormControl(null),
            phone: new FormControl(null),
            firmsCode: new FormControl(null),
            externalFirmsCode: new FormControl(null),
            operatingHours: new FormArray<FormGroup<OperatingHourFormControls>>([]),
            position: new FormGroup<LatLngFormControls>({ lat: new FormControl(null), lng: new FormControl(null) }),
            customers: new FormArray<FormControl<string | null>>([]),
            email: new FormArray<FormControl<string | null>>([]),
            awbPrefixes: new FormArray<FormControl<string | null>>([]),
            city: new FormControl(null),
            state: new FormControl(null),
            zip: new FormControl(null),
            country: new FormControl(null),
            amsNominationEmail: new FormControl(null),
            workingDays: new FormControl(null),
            openTime: new FormControl(null),
            closeTime: new FormControl(null),
        });
    }

    public addFacility(): void {
        this.facilitiesArray.push(this.createFacilityGroup());
    }

    public removeFacility(index: number): void {
        const arr = this.facilitiesArray;
        if (arr.length > index) arr.removeAt(index);
    }

    removeArrayItem(arrayName: string, index: number): void {
        const arr = this.facilityInformationGroup.controls[arrayName];
        arr.removeAt(index);
    }

    public addOperatingHourFor(facilityIndex: number): void {
        this.getOperatingHoursArrayFor(facilityIndex).push(
            new FormGroup<OperatingHourFormControls>({
                days: new FormControl('', { nonNullable: true }),
                hour: new FormControl('', { nonNullable: true }),
            })
        );
    }

    public removeOperatingHourFor(facilityIndex: number, index: number): void {
        this.getOperatingHoursArrayFor(facilityIndex).removeAt(index);
    }

    public addCustomerFor(facilityIndex: number): void {
        const arr = this.getFacilityGroup(facilityIndex).controls.customers;
        arr.push(new FormControl('', { nonNullable: true }));
    }

    public addEmailFor(facilityIndex: number): void {
        const arr = this.getFacilityGroup(facilityIndex).controls.email;
        arr.push(new FormControl('', { nonNullable: true }));
    }

    public addAwbPrefixFor(facilityIndex: number): void {
        const arr = this.getFacilityGroup(facilityIndex).controls.awbPrefixes;
        arr.push(new FormControl('', { nonNullable: true }));
    }

    public removeArrayItemFor(facilityIndex: number, arrayName: string, index: number): void {
        const arr = this.getFacilityGroup(facilityIndex).controls[arrayName];
        arr.removeAt(index);
    }

    private toFacilityInformation(
        raw: ReturnType<FormGroup<FacilityInformationFormControls>['getRawValue']>
    ): FacilityInformation | undefined {
        const pruned = pruneValue(raw);

        if (!pruned) {
            return undefined;
        }
        const out: Partial<FacilityInformation> = {};

        const isObj = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object';

        if (typeof pruned.airportName === 'string' && pruned.airportName.trim() !== '')
            out.airportName = pruned.airportName.trim();
        if (typeof pruned.airportCode === 'string' && pruned.airportCode.trim() !== '')
            out.airportCode = pruned.airportCode.trim();
        if (typeof pruned.address1 === 'string' && pruned.address1.trim() !== '') out.address1 = pruned.address1.trim();
        if (typeof pruned.address2 === 'string' && pruned.address2.trim() !== '') out.address2 = pruned.address2.trim();
        if (typeof pruned.phone === 'string' && pruned.phone.trim() !== '') out.phone = pruned.phone.trim();
        if (typeof pruned.firmsCode === 'string' && pruned.firmsCode.trim() !== '')
            out.firmsCode = pruned.firmsCode.trim();
        if (typeof pruned.externalFirmsCode === 'string' && pruned.externalFirmsCode.trim() !== '')
            out.externalFirmsCode = pruned.externalFirmsCode.trim();

        if (Array.isArray(pruned.operatingHours)) {
            const hours = pruned.operatingHours
                .map((hour) => {
                    if (!isObj(hour)) return undefined;
                    const d = hour['days'];
                    const h = hour['hour'];
                    const entry: { days?: string; hour?: string } = {};
                    if (typeof d === 'string' && d.trim() !== '') entry.days = d.trim();
                    if (typeof h === 'string' && h.trim() !== '') entry.hour = h.trim();
                    return Object.keys(entry).length ? entry : undefined;
                })
                .filter((x): x is { days?: string; hour?: string } => x !== undefined);
            if (hours.length) out.operatingHours = hours;
        }

        if (isObj(pruned.position) && pruned.position['lat'] != null && pruned.position['lng'] != null) {
            const lat = Number(pruned.position['lat']);
            const lng = Number(pruned.position['lng']);
            if (!Number.isNaN(lat) && !Number.isNaN(lng)) out.position = { lat, lng };
        }

        if (Array.isArray(pruned.customers)) {
            const customers = pruned.customers.filter(
                (v: unknown): v is string => typeof v === 'string' && v.trim() !== ''
            );
            if (customers.length) out.customers = customers;
        }

        if (Array.isArray(pruned.email)) {
            const emails = pruned.email.filter((v: unknown): v is string => typeof v === 'string' && v.trim() !== '');
            if (emails.length) out.email = emails;
        }

        if (Array.isArray(pruned.awbPrefixes)) {
            const awbs = pruned.awbPrefixes.filter(
                (v: unknown): v is string => typeof v === 'string' && v.trim() !== ''
            );
            if (awbs.length) out.awbPrefixes = awbs;
        }

        if (typeof pruned.city === 'string' && pruned.city.trim() !== '') out.city = pruned.city.trim();
        if (typeof pruned.state === 'string' && pruned.state.trim() !== '') out.state = pruned.state.trim();
        if (typeof pruned.zip === 'string' && pruned.zip.trim() !== '') out.zip = pruned.zip.trim();
        if (typeof pruned.country === 'string' && pruned.country.trim() !== '') out.country = pruned.country.trim();
        if (typeof pruned.amsNominationEmail === 'string' && pruned.amsNominationEmail.trim() !== '')
            out.amsNominationEmail = pruned.amsNominationEmail.trim();
        if (typeof pruned.workingDays === 'string' && pruned.workingDays.trim() !== '')
            out.workingDays = pruned.workingDays.trim();
        if (typeof pruned.openTime === 'string' && pruned.openTime.trim() !== '') out.openTime = pruned.openTime.trim();
        if (typeof pruned.closeTime === 'string' && pruned.closeTime.trim() !== '')
            out.closeTime = pruned.closeTime.trim();

        return Object.keys(out).length ? (out as FacilityInformation) : undefined;
    }

    public getFacilityInformationPayload(): FacilityInformation[] | null {
        const arr = this.facilitiesArray;
        if (!arr.length) return null;
        const payloads = arr.controls
            .map((g) => this.toFacilityInformation(g.getRawValue()))
            .filter((p): p is FacilityInformation => p !== undefined);
        return payloads.length ? payloads : null;
    }
}
